import { useFormik } from "formik";
import {
  superadminUpdateCollaborationHistory,
  superadminUpdateCompanyDocuments,
} from "helpers/backend_helpers";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import { Indonesian } from "flatpickr/dist/l10n/id.js";
import moment from "moment";

const HistoryCollaborationModalUpdate = ({
  show,
  onCloseClick,
  getData,
  data,
}: any) => {
  const [file, setFile] = useState<File | null>(null);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      projectName: data?.projectName || "",
      projectValue: data?.projectValue || "",
      projectLocation: data?.projectLocation || "",
      startDate: data?.startDate || "",
      endDate: data?.endDate || "",
      note: data?.note || "",
    },
    validationSchema: Yup.object({
      projectName: Yup.string().required("Nama Proyek harus diisi!"),
      projectValue: Yup.number()
        .required("Nilai Proyek harus diisi!")
        .positive("Nilai Proyek harus berupa angka positif"),
      projectLocation: Yup.string().required("Lokasi Proyek harus diisi!"),
      startDate: Yup.date().required("Tanggal Mulai harus diisi!"),
      endDate: Yup.date()
        .required("Tanggal Selesai harus diisi!")
        .min(
          Yup.ref("startDate"),
          "Tanggal Selesai tidak boleh sebelum Tanggal Mulai",
        ),
      note: Yup.string(),
    }),

    onSubmit: (values) => {
      const formData = new FormData();
      if (file) {
        formData.append("file", file);
      }
      formData.append("projectName", values.projectName);
      formData.append("projectValue", values.projectValue);
      formData.append("projectLocation", values.projectLocation);
      formData.append("startDate", values.startDate);
      formData.append("endDate", values.endDate);

      if (values.note) {
        formData.append("note", values.note);
      }

      superadminUpdateCollaborationHistory(data?._id, formData)
        .then((response: any) => {
          onCloseClick();
          getData();
          toast.success(<div>Riwayat Kolaborasi berhasil diperbarui!</div>);
          validation.resetForm();
          setFile(null);
        })
        .catch(() => {});
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (!show) {
      validation.resetForm();
      setFile(null);
    }
  }, [show]);

  return (
    <Modal fade={true} isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalHeader className="bg-light p-3" toggle={onCloseClick}>
        Edit Riwayat Kolaborasi <strong>{data?.projectName}</strong>
      </ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <div className="mb-3">
            <Label htmlFor="file-input" className="form-label">
              Pilih File{" "}
              <span className="text-muted small">
                (Kosongkan jika tidak ingin mengganti)
              </span>
            </Label>
            <Input
              type="file"
              className="form-control"
              id="file-input"
              accept=".pdf"
              onChange={handleFileChange}
            />
            <div className="form-text">Format file: .pdf</div>
          </div>

          {file && (
            <Alert color="info" className="mb-3">
              <i className="ri-file-pdf-line align-middle me-2"></i>
              File baru: <strong>{file.name}</strong>
            </Alert>
          )}

          {!file && data?.fileName && (
            <Alert color="secondary" className="mb-3">
              <i className="ri-file-pdf-line align-middle me-2"></i>
              File saat ini: <strong>{data.fileName}</strong>
            </Alert>
          )}

          <div className="mb-3">
            <Label htmlFor="projectName" className="form-label">
              Nama Proyek <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              className="form-control"
              id="projectName"
              name="projectName"
              placeholder="Nama Proyek"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.projectName || ""}
              invalid={
                validation.touched.projectName && validation.errors.projectName
                  ? true
                  : false
              }
            />
            {validation.errors.projectName && validation.touched.projectName ? (
              <FormFeedback type="invalid">
                {validation.errors.projectName}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="projectValue" className="form-label">
              Nilai Proyek <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              className="form-control"
              id="projectValue"
              name="projectValue"
              placeholder="Nilai Proyek"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.projectValue || ""}
              invalid={
                validation.touched.projectValue &&
                validation.errors.projectValue
                  ? true
                  : false
              }
            />
            {validation.errors.projectValue &&
            validation.touched.projectValue ? (
              <FormFeedback type="invalid">
                {validation.errors.projectValue}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="projectLocation" className="form-label">
              Lokasi Proyek <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              className="form-control"
              id="projectLocation"
              name="projectLocation"
              placeholder="Lokasi Proyek"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.projectLocation || ""}
              invalid={
                validation.touched.projectLocation &&
                validation.errors.projectLocation
                  ? true
                  : false
              }
            />
            {validation.errors.projectLocation &&
            validation.touched.projectLocation ? (
              <FormFeedback type="invalid">
                {validation.errors.projectLocation}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="startDate" className="form-label">
              Tanggal Mulai <span className="text-danger">*</span>
            </Label>
            <Flatpickr
              name="startDate"
              id="startDate"
              className={`form-control ${
                validation.errors.startDate && validation.touched.startDate
                  ? "is-invalid"
                  : ""
              }`}
              placeholder="Pilih Tanggal Mulai"
              options={{
                locale: Indonesian,
                altInput: true,
                altFormat: "d F Y",
                dateFormat: "Y-m-d",
              }}
              onChange={(date: any) =>
                validation.setFieldValue(
                  "startDate",
                  moment(date[0]).format("YYYY-MM-DD"),
                )
              }
              value={validation.values.startDate || ""}
            />
            {validation.errors.startDate && validation.touched.startDate ? (
              <FormFeedback type="invalid" className="d-block">
                {validation.errors.startDate}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="endDate" className="form-label">
              Tanggal Selesai <span className="text-danger">*</span>
            </Label>
            <Flatpickr
              name="endDate"
              id="endDate"
              className={`form-control ${
                validation.errors.endDate && validation.touched.endDate
                  ? "is-invalid"
                  : ""
              }`}
              placeholder="Pilih Tanggal Selesai"
              options={{
                locale: Indonesian,
                altInput: true,
                altFormat: "d F Y",
                dateFormat: "Y-m-d",
                minDate: validation.values.startDate || "",
              }}
              onChange={(date: any) =>
                validation.setFieldValue(
                  "endDate",
                  moment(date[0]).format("YYYY-MM-DD"),
                )
              }
              value={validation.values.endDate || ""}
            />
            {validation.errors.endDate && validation.touched.endDate ? (
              <FormFeedback type="invalid" className="d-block">
                {validation.errors.endDate}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <label htmlFor="note" className="form-label">
              Keterangan
            </label>
            <Input
              type="textarea"
              className="form-control"
              id="note"
              name="note"
              rows={3}
              placeholder="Keterangan"
              value={validation.values.note || ""}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-soft-danger waves-effect waves-light material-shadow-none btn-sm"
            onClick={onCloseClick}
          >
            Batal
          </button>
          <button
            type="submit"
            className="btn btn-soft-success waves-effect waves-light material-shadow-none btn-sm"
          >
            Simpan
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

HistoryCollaborationModalUpdate.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  getData: PropTypes.func,
  data: PropTypes.any,
};

export default HistoryCollaborationModalUpdate;
